import { useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
} from '@mui/material';
import { StatusIcon } from '../status-icon';
import type {
  AccordionStepProps,
  DescriptionStepProps,
  HeadingProps,
} from './interfaces';
import { ContentBox } from '@/components/shared/content-box';

const Heading = ({
  title,
  description,
  stepStatus,
  disabled,
  accordion,
}: HeadingProps) => {
  const opacity = disabled && !accordion ? 0.38 : 1;

  return (
    <Stack direction="row" spacing={2}>
      {stepStatus && !disabled && <StatusIcon status={stepStatus} />}
      <Stack gap={2}>
        <Typography
          variant="h2"
          sx={{ opacity }}
          color={!disabled ? 'primary.dark' : ''}
        >
          {title}
        </Typography>
        {!disabled && description}
      </Stack>
    </Stack>
  );
};

export const AccordionStep = ({
  children,
  title,
  description,
  stepStatus,
  disabled = false,
  defaultExpanded = false,
  onChange,
  isExpanded,
}: AccordionStepProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const isControlled = isExpanded !== undefined;
  const expandedState = isControlled ? isExpanded : expanded;

  return (
    <Accordion
      disabled={disabled}
      slotProps={{ transition: { unmountOnExit: false } }}
      expanded={expandedState}
      onChange={(e, next) => {
        onChange?.(e, next);
        if (!isControlled) setExpanded(next);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Heading
          title={title}
          description={description}
          stepStatus={stepStatus}
          disabled={disabled}
          accordion
        />
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

export const DescriptionStep = ({
  title,
  description,
  stepStatus,
  disabled = false,
}: DescriptionStepProps) => {
  return (
    <ContentBox
      sx={{
        bgcolor: disabled ? 'transparent' : 'background.paper',
        borderRadius: 1,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Heading
        title={title}
        description={description}
        stepStatus={stepStatus}
        disabled={disabled}
      />
    </ContentBox>
  );
};
